/**
 * SchedulingScreen — Date/time picker + confirm.
 *
 * M2-UX-P0-008: Build SchedulingScreen.
 * UI-INV-005: Leaf component, props only, no engine imports.
 *
 * Design decision: SchedulingConfirmScreen (M2-UX-P0-009) is combined with
 * this screen as a single "pick + confirm" flow.
 */
import { useState, useRef } from "react";
import type { EditableRetreatSchedule } from "@retreat/core";
import type { WakeOffset } from "../constants";
import { VALID_WAKE_OFFSETS } from "../constants";
import {
  totalSeconds,
  formatDuration,
  formatDateLabel,
  formatEndTime,
  formatTime12h,
} from "../utils/scheduleUtils";
import { MetaStrip } from "../components/MetaStrip";
import styles from "./SchedulingScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { PrimaryAction } from "../primitives/PrimaryAction";

type Props = {
  schedule: EditableRetreatSchedule;
  isPushSupported: boolean;
  isStandalone: boolean;
  onConfirm: (scheduledAt: number, wakeOffset: WakeOffset) => void;
  onBack: () => void;
};

type WakeOffsetCopy = { label: string; confirmation: string };

const WAKE_OFFSET_COPY: Record<WakeOffset, WakeOffsetCopy> = {
  '30min': { label: '30 min before',         confirmation: "We'll ring this device 30 minutes before." },
  '15min': { label: '15 min before',         confirmation: "We'll ring this device 15 minutes before." },
  '5min':  { label: '5 min before',          confirmation: "We'll ring this device 5 minutes before."  },
  'self':  { label: "I'll set my own alarm", confirmation: "Open the app when you're ready to begin."  },
};

function dayLabel(date: Date): string {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (date.toDateString() === today.toDateString()) return "today";
  if (date.toDateString() === tomorrow.toDateString()) return "tomorrow";
  return date.toLocaleDateString("en-US", { weekday: "long" });
}


type NotifContext = {
  wakeOffset: WakeOffset;
  notifPerm: NotificationPermission;
  isOnline: boolean;
  isPushSupported: boolean;
  isStandalone: boolean;
};

function shouldRequestNotifPermission({ wakeOffset, isOnline, isPushSupported, notifPerm }: NotifContext): boolean {
  return (
    wakeOffset !== "self" &&
    isOnline &&
    isPushSupported &&
    notifPerm !== "granted" &&
    "Notification" in window
  );
}

function getNotifCopy({ wakeOffset, notifPerm, isOnline, isPushSupported, isStandalone }: NotifContext): string {
  if (wakeOffset === 'self') return "The retreat begins automatically when you open the app.";
  if (!isStandalone && /iP(hone|ad|od)/.test(navigator.userAgent)) {
    return "Install to your home screen for a background alarm. Otherwise keep the app open.";
  }
  if (!isPushSupported) return "Keep the app open — we'll start on time.";
  if (!isOnline) return "No connection right now — alarm will register when you reconnect. Keep the app open as backup.";
  if (notifPerm === 'granted') return "Alarm set — fires even if the app is closed.";
  return "Keep the app open — or allow notifications for a background alarm.";
}

function localDateStr(date: Date): string {
  // toISOString() returns UTC — use local components to avoid off-by-one in UTC+ timezones
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const NEXT_TIME_BUFFER_MINS = 5;
const NEXT_TIME_SLOT_MINS = 15;

// Returns the next clean slot at least NEXT_TIME_BUFFER_MINS from now.
function nextRoundTime(): string {
  const now = new Date();
  const totalMins = now.getHours() * 60 + now.getMinutes() + NEXT_TIME_BUFFER_MINS;
  const roundedMins = Math.ceil(totalMins / NEXT_TIME_SLOT_MINS) * NEXT_TIME_SLOT_MINS;
  const hh = Math.floor(roundedMins / 60) % 24;
  const mm = roundedMins % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

const DEFAULT_SUGGESTED_START_HOUR = 5.5; // 05:30 — pre-dawn start typical for silent retreats


function suggestedTimeStr(suggestedStartHour: number | undefined): string {
  const h = suggestedStartHour ?? DEFAULT_SUGGESTED_START_HOUR;
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${hh.toString().padStart(2, "0")}:${mm.toString().padStart(2, "0")}`;
}

function defaultDateStr(suggestedStartHour: number | undefined): string {
  const timeStr = suggestedTimeStr(suggestedStartHour);
  const [hh, mm] = timeStr.split(":").map(Number);
  const todayAtSuggestedTime = new Date();
  todayAtSuggestedTime.setHours(hh, mm, 0, 0);
  // If today's suggested start has already passed, default to tomorrow
  if (todayAtSuggestedTime.getTime() <= Date.now()) {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return localDateStr(tomorrow);
  }
  return localDateStr(new Date());
}

export function SchedulingScreen({ schedule, isPushSupported, isStandalone, onConfirm, onBack }: Props) {
  const [dateStr, setDateStr] = useState(() => defaultDateStr(schedule.suggestedStartHour));
  const [timeStr, setTimeStr] = useState(() => suggestedTimeStr(schedule.suggestedStartHour));
  const dateInputRef = useRef<HTMLInputElement>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);
  const [wakeOffset, setWakeOffset] = useState<WakeOffset>('30min');
  const [notifPerm, setNotifPerm] = useState<NotificationPermission>(() =>
    'Notification' in window ? Notification.permission : 'denied',
  );
  const [isConfirming, setIsConfirming] = useState(false);

  const duration = totalSeconds(schedule);
  const durationLabel = formatDuration(duration);

  const [year, month, day] = dateStr.split("-").map(Number);
  const [hours, minutes] = timeStr.split(":").map(Number);
  const scheduledDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

  const dateLabel = formatDateLabel(scheduledDate);
  const endLabel = formatEndTime(scheduledDate, duration);
  const isPast = scheduledDate.getTime() <= Date.now();
  const isOnline = navigator.onLine;
  const todayStr = localDateStr(new Date());
  const defaultTime = suggestedTimeStr(schedule.suggestedStartHour);
  const isDefaultValues = dateStr === defaultDateStr(schedule.suggestedStartHour) && timeStr === defaultTime;

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    setDateStr(newDate);
    // When switching to today, snap time forward if the current time is already past
    if (newDate === todayStr) {
      const [h, m] = timeStr.split(":").map(Number);
      const candidate = new Date();
      candidate.setHours(h, m, 0, 0);
      if (candidate.getTime() <= Date.now()) {
        setTimeStr(nextRoundTime());
      }
    }
  };

  const handleConfirm = async () => {
    if (isPast || isConfirming) return;
    setIsConfirming(true);
    try {
      if (shouldRequestNotifPermission({ wakeOffset, notifPerm, isOnline, isPushSupported, isStandalone })) {
        try {
          const perm = await Notification.requestPermission();
          setNotifPerm(perm);
        } catch {
          // Permission dialog unavailable (test env, iOS non-PWA) — proceed
        }
      }
      onConfirm(scheduledDate.getTime(), wakeOffset);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className={styles.screen} data-testid={TEST_IDS.SCHEDULING_SCREEN}>
      <MetaStrip label="Schedule" onBack={onBack} />

      <div className={styles.content}>
        <h1 className={styles.heading}>When does it begin?</h1>

        {/* Date field */}
        <div className={styles.field} onClick={() => dateInputRef.current?.focus()}>
          <div className={styles.fieldLabel}>Date</div>
          <div className={styles.dateDisplay}>{dateLabel}</div>
          <input
            ref={dateInputRef}
            id="scheduling-date-input"
            data-testid={TEST_IDS.SCHEDULING_DATE_INPUT}
            type="date"
            className={styles.nativeInput}
            value={dateStr}
            onChange={handleDateChange}
            min={localDateStr(new Date())}
            aria-label="Pick a date"
          />
        </div>

        {/* Time field */}
        <div className={styles.field} onClick={() => timeInputRef.current?.focus()}>
          <div className={styles.fieldLabel}>Begin at</div>
          <div className={styles.timeDisplay}>{formatTime12h(scheduledDate.getTime())}</div>
          <input
            ref={timeInputRef}
            id="scheduling-time-input"
            data-testid={TEST_IDS.SCHEDULING_TIME_INPUT}
            type="time"
            className={styles.nativeInput}
            value={timeStr}
            onChange={(e) => setTimeStr(e.target.value)}
            aria-label="Pick a time"
          />
        </div>

        {schedule.suggestedStartHour !== undefined && isDefaultValues && (
          <div className={styles.suggestedHint}>
            suggested start for this retreat
          </div>
        )}

        {/* Wake offset field */}
        <div className={styles.field}>
          <div className={styles.fieldLabel}>Wake-up alarm</div>
          <div className={styles.selectDisplay}>{WAKE_OFFSET_COPY[wakeOffset].label}</div>
          <select
            id="scheduling-wake-offset"
            className={styles.nativeInput}
            value={wakeOffset}
            onChange={(e) => setWakeOffset(e.target.value as WakeOffset)}
            aria-label="Wake-up notification timing"
          >
            {VALID_WAKE_OFFSETS.map((o) => (
              <option key={o} value={o}>{WAKE_OFFSET_COPY[o].label}</option>
            ))}
          </select>
        </div>

        <div className={styles.durationEcho}>
          {durationLabel} · ends {endLabel}
        </div>

        {!isPast && (
          <div className={styles.confirmSummary}>
            You're sitting {dayLabel(scheduledDate)} at {formatTime12h(scheduledDate.getTime())}.{" "}
            {WAKE_OFFSET_COPY[wakeOffset].confirmation}
          </div>
        )}

        {isPast && (
          <div className={styles.pastError}>
            This time has already passed — please choose a future time.
          </div>
        )}

        <div className={styles.ctas}>
          <PrimaryAction
            id="scheduling-confirm-btn"
            testId={TEST_IDS.SCHEDULING_CONFIRM_BTN}
            onClick={handleConfirm}
            disabled={isPast || isConfirming}
          >
            {isConfirming ? "Setting up…" : isPast ? "Choose a future time" : "Confirm →"}
          </PrimaryAction>
        </div>

        <div className={styles.hint}>
          {getNotifCopy({ wakeOffset, notifPerm, isOnline, isPushSupported, isStandalone })}
        </div>
      </div>
    </div>
  );
}
